import { useTranslation } from 'react-i18next';
import { CAMERA_STATUS_COLOR, type ICamera } from '@interfaces/ICameras';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { StatusDot } from '@components/ui/StatusDot/StatusDot';
import { VideoIcon, SearchIconSm, AlertIcon } from '@utils/icons';
import styles from './CameraList.module.css';

interface ICameraListProps {
  cameras: ICamera[];
  counts: { total: number; live: number; alerts: number };
  search: string;
  onSearchChange: (value: string) => void;
  floorOptions: string[];
  floorFilter: string | null;
  onFloorFilterChange: (value: string | null) => void;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}

export function CameraList({
  cameras,
  counts,
  search,
  onSearchChange,
  floorOptions,
  floorFilter,
  onFloorFilterChange,
  selectedId,
  onSelect,
}: ICameraListProps) {
  const { t } = useTranslation();

  return (
    <aside className={styles.panel}>
      <div className={styles.titleRow}>
        <VideoIcon size={15} />
        <h2 className={styles.title}>{t('cameras.list.title')}</h2>
      </div>

      <div className={styles.stats}>
        <Stat tone="neutral" value={counts.total} label={t('cameras.stats.total')} />
        <Stat tone="live" value={counts.live} label={t('cameras.stats.live')} />
        <Stat tone="alert" value={counts.alerts} label={t('cameras.stats.alerts')} />
      </div>

      <label className={styles.searchField}>
        <SearchIconSm />
        <input
          type="search"
          className={styles.searchInput}
          placeholder={t('cameras.list.searchPlaceholder')}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </label>

      <div className={styles.chips} role="group">
        <button
          type="button"
          className={styles.chip}
          data-active={floorFilter === null}
          onClick={() => onFloorFilterChange(null)}
        >
          {t('cameras.filter.all')}
        </button>
        {floorOptions.map((floor) => (
          <button
            key={floor}
            type="button"
            className={styles.chip}
            data-active={floorFilter === floor}
            onClick={() => onFloorFilterChange(floor)}
          >
            {floor}
          </button>
        ))}
      </div>

      <ScrollArea className={styles.listScroll}>
        <ul className={styles.list}>
          {cameras.length === 0 && (
            <li className={styles.empty}>{t('cameras.list.empty')}</li>
          )}
          {cameras.map((camera) => {
            const isSelected = camera.id === selectedId;
            return (
              <li key={camera.id}>
                <button
                  type="button"
                  className={styles.row}
                  data-selected={isSelected}
                  onClick={() => onSelect(isSelected ? null : camera.id)}
                >
                  <StatusDot
                    color={CAMERA_STATUS_COLOR[camera.status]}
                    animate={camera.status !== 'offline'}
                  />
                  <div className={styles.rowBody}>
                    <span className={styles.cameraName}>{camera.name}</span>
                    <span className={styles.cameraMeta}>
                      {camera.id} · {camera.zone}
                    </span>
                  </div>
                  {camera.status === 'alert' && (
                    <span className={styles.rowAlert} aria-hidden>
                      <AlertIcon size={13} />
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </ScrollArea>
    </aside>
  );
}

interface IStatProps {
  tone: 'neutral' | 'live' | 'alert';
  value: number;
  label: string;
}

function Stat({ tone, value, label }: IStatProps) {
  return (
    <div className={styles.stat} data-tone={tone}>
      <span className={styles.statValue}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
    </div>
  );
}
